import classNames from 'classnames';
import { type CSSProperties } from 'react';
import styled, { keyframes } from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { DiamondPatternBackground } from '@sorare/core/src/atoms/ui/DiamondPatternBackground';
import {
  desktopAndAbove,
  mediumLargeDesktopAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { DIAMOND_HEIGHT, DIAMOND_WIDTH } from '../utils.ts';

const pulse = keyframes`
  0% {
    filter: blur(2px);
  }
  50% {
    filter: blur(1px) brightness(1.2);
  }
  100% {
    filter: blur(2px);
  }
`;

const Root = styled(Vertical)`
  align-items: flex-start;
  --spacing: 2;
  --row-height: calc(${DIAMOND_HEIGHT} * var(--spacing) * 1px);
  --row-width: calc(${DIAMOND_WIDTH} * (var(--spacing) + 1) * 1px);

  background:
    linear-gradient(
      to bottom,
      var(--c-black) 0%,
      transparent 300px,
      transparent,
      transparent calc(100% - 300px),
      var(--c-black) 100%
    ),
    var(--decoration, linear-gradient(to top, transparent, transparent))
      no-repeat center center / cover,
    var(--background-image) no-repeat center center / cover;
  @media ${mediumLargeDesktopAndAbove} {
    --spacing: 3;
  }
  @media ${desktopAndAbove} {
    background:
      linear-gradient(
        to right,
        transparent,
        transparent calc(100% - 100px),
        var(--c-black) 100%
      ),
      linear-gradient(
        to bottom,
        rgba(0, 0, 0, 1) 0%,
        transparent 300px,
        transparent,
        transparent calc(100% - 300px),
        rgba(0, 0, 0, 1) 100%
      ),
      var(--background-image) no-repeat center center / cover;
  }
`;

const Line = styled.line`
  fill: none;
  stroke: black;
  stroke-opacity: 0.5;
  stroke-width: 6;

  &.active {
    stroke: #02dade;
    stroke-opacity: 1;
    filter: blur(2px);
    animation: ${pulse} 10s infinite;
  }
`;

const BoardContent = styled.div`
  display: flex;
  flex-direction: column-reverse;
  position: relative;
  margin: auto;
  z-index: 0;
`;

const Row = styled(Vertical)`
  height: var(--row-height);
  width: var(--row-width);
  align-items: center;
  justify-content: center;
  position: relative;
  pointer-events: none;
  > * {
    pointer-events: auto;
  }
`;

const LineSvg = styled.svg`
  position: absolute;
  z-index: -1;
  bottom: 50%;
  left: ${DIAMOND_WIDTH / 2}px;
  height: var(--row-height);
  width: calc(var(--row-width) - ${DIAMOND_WIDTH}px);
`;

const Content = styled(Vertical)`
  flex: 1;
  position: relative;
  width: 100%;
  padding: 100px 0;
  @media ${desktopAndAbove} {
    padding: 0;
  }
`;

type BoardItemsProps<
  T extends { id: string; aasmState: ManagerProgressionTaskState },
> = {
  items: T[];
  renderItem: ({ item, index }: { item: T; index: number }) => React.ReactNode;
};

export const FullHeightBoard = <
  T extends { id: string; aasmState: ManagerProgressionTaskState },
>({
  backgroundImage,
  mobileDecoration,
  items,
  renderItem,
  children,
}: {
  backgroundImage: string;
  mobileDecoration?: string | null;
  children?: React.ReactNode;
} & BoardItemsProps<T>) => {
  return (
    <Root
      style={
        {
          '--background-image': `url(${backgroundImage})`,
          '--decoration': mobileDecoration
            ? `url(${mobileDecoration})`
            : undefined,
        } as CSSProperties
      }
    >
      <Content>
        <DiamondPatternBackground
          width={DIAMOND_WIDTH}
          height={DIAMOND_HEIGHT}
        />
        <BoardContent>
          {items.map((item, index) => {
            const isEven = index % 2 === 0;
            const showLine = index < items.length - 1;
            return (
              <Row
                key={item.id}
                style={{ alignItems: isEven ? 'flex-start' : 'flex-end' }}
              >
                {renderItem({ item, index })}
                {!!showLine && (
                  <LineSvg>
                    <Line
                      className={classNames({
                        active:
                          item.aasmState ===
                          ManagerProgressionTaskState.CLAIMED,
                      })}
                      x1={isEven ? '100%' : 0}
                      y1={0}
                      x2={isEven ? 0 : '100%'}
                      y2="100%"
                    />
                  </LineSvg>
                )}
              </Row>
            );
          })}
        </BoardContent>
        {children}
      </Content>
    </Root>
  );
};
